import { countryFromVatNumber } from '../../../../common/utils/tax/vatCountry.js'

/*
 * The contact card for a supplier the org does not have yet, prefilled from
 * the document (ExtractionPanel's "create supplier"). Two steps:
 *
 *   newSupplierDefaults   what the document says, synchronously. The country
 *                         is the printed one, else the one the VAT number's
 *                         prefix names (FR40… → FR), else the org's own — a
 *                         receipt with no address abroad is a domestic one.
 *                         The country is what the VAT rules key on, for this
 *                         receipt and the next ones.
 *   fillFromRegistry      what the national registry knows (company-info), for
 *                         the fields the document left empty. Never replaces a
 *                         value the document printed.
 *
 * `config` is extractionData.expense.newSupplier.
 */

const readPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj)

export const newSupplierDefaults = (config, supplier, orgCountry) => {
    const defaults = { ...config.defaults }
    for (const [field, key] of Object.entries(config.map)) {
        if (supplier?.[key]) defaults[field] = supplier[key]
    }
    if (!defaults.country) defaults.country = countryFromVatNumber(supplier?.taxId) || orgCountry || undefined
    if (!defaults.country) delete defaults.country
    return defaults
}

/**
 * @param {object} defaults   from newSupplierDefaults
 * @param {object} config     extractionData.expense.newSupplier
 * @param {(url: string) => Promise<any>} get   getMe; resolves to the body, or an error response
 * @param {{ timeoutMs?: number }} [opts]   the card opens without waiting longer than this
 * @returns {Promise<object>} defaults, with the empty fields the registry could fill
 */
export const fillFromRegistry = async (defaults, config, get, { timeoutMs = 3000 } = {}) => {
    if (!defaults.taxId || !defaults.country) return defaults
    let company
    let timer
    try {
        const url = `company-info/${encodeURIComponent(defaults.country)}/${encodeURIComponent(defaults.taxId)}`
        const tooSlow = new Promise(resolve => { timer = setTimeout(resolve, timeoutMs) })
        company = await Promise.race([get(url), tooSlow])
    } catch {
        return defaults
    } finally {
        clearTimeout(timer)
    }
    // A registry that has no such company, no provider for the country, or is
    // down answers an error response; only a company has a companyId.
    if (!company?.companyId) return defaults

    const filled = { ...defaults }
    for (const [field, path] of Object.entries(config.registry ?? {})) {
        const value = readPath(company, path)
        if (!filled[field] && value) filled[field] = value
    }
    if (!filled.countryName && company.countryName && company.country === filled.country) filled.countryName = company.countryName
    return filled
}

/*
 * The country picker shows `countryName`, not the ISO code, so a card opened
 * with only `country: 'FR'` shows an empty field. The name is the one the
 * picker's own list gives, in the org's language.
 *
 * @param {object} defaults
 * @param {() => Promise<{ id: string, name: string }[]|undefined>} listCountries   the /country list
 */
export const withCountryName = async (defaults, listCountries) => {
    if (!defaults.country || defaults.countryName) return defaults
    try {
        const hit = (await listCountries())?.find(c => c.id === defaults.country)
        return hit?.name ? { ...defaults, countryName: hit.name } : defaults
    } catch {
        return defaults
    }
}
